import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ConnectionStats, StatsWindow } from './useApiObserve';
import { errorRateColor } from './helpers';

type Props = {
  stats: ConnectionStats | undefined;
  isLoading: boolean;
  window: StatsWindow;
  onWindowChange: (w: StatsWindow) => void;
};

const WINDOWS: StatsWindow[] = ['1h', '6h', '24h', '7d'];

export default function StatsPanel({ stats, isLoading, window, onWindowChange }: Props) {
  const chartData = (stats?.calls_per_hour || []).map((h) => ({
    label: new Date(h.hour).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' }),
    success: Math.max(0, h.count - (h.error_count || 0)),
    errors: h.error_count || 0,
    count: h.count,
  }));

  const hasErrorSeries = chartData.some((d) => d.errors > 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => onWindowChange(w)}
            className={`px-3 py-1 text-sm rounded-lg border ${
              window === w ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700'
            }`}
          >
            {w}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading stats…</div>
      ) : !stats ? (
        <div className="text-sm text-gray-500 rounded-lg border border-dashed p-6 text-center">
          No stats for this connection in the selected window.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Total calls" value={String(stats.call_count)} />
            <StatCard
              label="Error rate"
              value={stats.error_rate_pct != null ? `${stats.error_rate_pct}%` : 'N/A'}
              valueClass={errorRateColor(stats.error_rate_pct)}
            />
            <StatCard
              label="p95 latency"
              value={stats.p95_latency_ms != null ? `${stats.p95_latency_ms} ms` : 'N/A'}
            />
            <StatCard
              label="Active since"
              value={
                stats.active_since
                  ? new Date(stats.active_since).toLocaleString()
                  : 'Unknown'
              }
            />
          </div>

          {chartData.length > 0 ? (
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  {hasErrorSeries ? (
                    <>
                      <Area
                        type="monotone"
                        dataKey="success"
                        stackId="1"
                        stroke="#22c55e"
                        fill="#bbf7d0"
                      />
                      <Area
                        type="monotone"
                        dataKey="errors"
                        stackId="1"
                        stroke="#ef4444"
                        fill="#fecaca"
                      />
                    </>
                  ) : (
                    <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#c7d2fe" />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-xs text-gray-500 text-center py-4">No hourly buckets in window</div>
          )}

          {stats.top_errors?.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase mb-2">Top errors</div>
              <ul className="text-sm space-y-1">
                {stats.top_errors.map((e) => (
                  <li key={e.error_message} className="flex justify-between gap-2">
                    <span className="truncate text-gray-800" title={e.error_message}>
                      {e.error_message}
                    </span>
                    <span className="text-gray-500 flex-shrink-0">{e.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  valueClass = 'text-gray-900',
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-3 bg-white">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}
